import { createRoute, Link, useNavigate, useSearch } from "@tanstack/solid-router";
import { createSignal, Show } from "solid-js";
import { authRoute } from "./route";
import { auth } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";
import { msg } from "@lingui/core/macro";
import { Button } from "@/components/ui/button";
import { Label, TextField } from "@/components/ui/text-field";
import { Logo } from "@/components/ui/logo";
import {
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { GlassSurface } from "@/components/ui/glass-surface";
import { AnimatedSection } from "@/components/ui/animated-section";

function ResetPasswordPage() {
  const navigate = useNavigate();
  const search = useSearch({ from: "/_auth/reset-password" });
  const { t } = useI18n();
  const [password, setPassword] = createSignal("");
  const [confirm, setConfirm] = createSignal("");
  const [error, setError] = createSignal<string | null>(null);
  const [loading, setLoading] = createSignal(false);

  const handleSubmit = async (e: SubmitEvent) => {
    e.preventDefault();
    if (password() !== confirm()) {
      setError(t(msg`Passwords do not match`));
      return;
    }
    const { token } = search();
    if (!token) {
      setError(t(msg`Invalid or expired recovery link`));
      return;
    }
    setError(null);
    setLoading(true);
    try {
      await auth.resetPassword({
        new_password: password(),
        token,
      });
      await navigate({ to: "/login" });
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError(t(msg`Unexpected error. Please try again.`));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <GlassSurface borderEdge="accent">
      <CardHeader class="flex flex-col items-center space-y-4 pt-10 pb-6">
        <Link to="/" class="transition-transform hover:scale-105 active:scale-95">
          <Logo size="lg" />
        </Link>
        <CardTitle class="text-2xl">{t(msg`New Password`)}</CardTitle>
        <CardDescription class="text-center">
          {t(msg`Choose a new password for your account`)}
        </CardDescription>
      </CardHeader>

      <Show
        when={search().token}
        fallback={
          <CardContent class="space-y-4">
            <div class="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-center space-y-1">
              <p class="font-medium text-destructive">{t(msg`Invalid link`)}</p>
              <p class="text-muted-foreground">
                {t(msg`This recovery link is invalid or has expired. Please request a new one.`)}
              </p>
            </div>
            <CardFooter class="flex flex-col gap-3 px-0 pb-0">
              <Link to="/forgot-password" class="w-full">
                <Button variant="outline" class="w-full">
                  {t(msg`Request new link`)}
                </Button>
              </Link>
            </CardFooter>
          </CardContent>
        }
      >
        <form onSubmit={(e) => void handleSubmit(e)}>
          <CardContent class="space-y-4">
            <div class="space-y-2">
              <Label for="password">{t(msg`New Password`)}</Label>
              <TextField
                id="password"
                type="password"
                placeholder={t(msg`••••••••`)}
                autocomplete="new-password"
                required
                minLength={8}
                value={password()}
                onInput={(e) => setPassword(e.currentTarget.value)}
              />
            </div>
            <div class="space-y-2">
              <Label for="confirm">{t(msg`Confirm Password`)}</Label>
              <TextField
                id="confirm"
                type="password"
                placeholder={t(msg`••••••••`)}
                autocomplete="new-password"
                required
                minLength={8}
                value={confirm()}
                onInput={(e) => setConfirm(e.currentTarget.value)}
              />
            </div>
            <AnimatedSection show={Boolean(error())}>
              <p class="text-sm text-destructive">{error()}</p>
            </AnimatedSection>
          </CardContent>

          <CardFooter class="flex flex-col gap-3">
            <Button type="submit" class="w-full" disabled={loading()}>
              {loading() ? t(msg`Saving…`) : t(msg`Save Password`)}
            </Button>
            <p class="text-center text-sm text-muted-foreground">
              <Link to="/login" class="text-primary underline-offset-4 hover:underline font-medium">
                {t(msg`Back to sign in`)}
              </Link>
            </p>
            <p class="text-center text-xs text-muted-foreground/60 mt-2">
              <Link to="/" class="hover:text-primary transition-colors">
                {t(msg`← Back to home`)}
              </Link>
            </p>
          </CardFooter>
        </form>
      </Show>
    </GlassSurface>
  );
}

export const resetPasswordRoute = createRoute({
  getParentRoute: () => authRoute,
  path: "/reset-password",
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : undefined,
  }),
  component: ResetPasswordPage,
});
